import type {
	PriorityMapping,
	PrioritySchemeValidationError,
	RequestMappings,
} from '../../common/ui/types';
import type {
	SavePrioritySchemeSuccessResponse,
	RefinedFetchError,
	SaveSchemeResult,
} from '../types';
import {
	fetchWithStatus,
	refineEditPrioritySchemeResponse,
	refineFetchError,
	refineValidationError,
} from '../utils';

type UpdatePriorities = {
	/**
	 * User generated content, if null no priorities will be added to the scheme.
	 */
	add?: {
		/**
		 * ids are unique and safe for jira instance.
		 */
		ids: string[];
	};
	/**
	 * User generated content, if null no priorities will be removed from the scheme.
	 */
	remove?: {
		/**
		 * ids are unique and safe for jira instance.
		 */
		ids: string[];
		/**
		 * Mappings will be executed in given order.
		 *
		 * This is used in src/packages/admin-pages/priority-schemes/src/controllers/priority-scheme-form/index.tsx
		 * It's being set as empty, delete this type as part of priority-scheme-mappings FF clean up
		 */
		mappings?: PriorityMapping[];
	};
};

type UpdateProjects = {
	/**
	 * User generated content, if null no projects will be added to the scheme.
	 */
	add?: {
		/**
		 * ids are unique and safe for jira instance.
		 */
		ids: string[];
	};
	/**
	 * User generated content, if null no projects will be removed from the scheme.
	 */
	remove?: {
		/**
		 * ids are unique and safe for jira instance.
		 */
		ids: string[];
	};
};

export type EditPrioritySchemeRequest = {
	/**
	 * User generated content, if null there will be no update to scheme name.
	 */
	name: string;
	/**
	 * User generated content, if null there will be no update to scheme description.
	 */
	description: string;
	/**
	 * User generated content, if null there will be no update to scheme priorities.
	 */
	priorities: UpdatePriorities;
	/**
	 * User generated content, if null there will be no update to the scheme's default priority.
	 */
	defaultPriorityId: string;
	/**
	 * User generated content, if null there will be no update to scheme projects.
	 */
	projects: UpdateProjects;
	mappings?: RequestMappings;
};

export const editPrioritySchemeRequest = async (
	schemeId: string,
	requestParams: EditPrioritySchemeRequest,
): Promise<SaveSchemeResult> => {
	try {
		const URL = `/rest/api/2/priorityscheme/${schemeId}`;
		const response: unknown = await fetchWithStatus(URL, {
			method: 'PUT',
			body: JSON.stringify(requestParams),
		});
		const responseData: SavePrioritySchemeSuccessResponse | undefined =
			refineEditPrioritySchemeResponse(schemeId, response);

		if (!responseData) {
			return {
				isSuccess: false,
			};
		}

		if ('task' in responseData.body) {
			return {
				isSuccess: true,
				isMigrationPending: true,
				data: responseData.body,
			};
		}
		return {
			isSuccess: true,
			isMigrationPending: false,
			data: responseData.body,
		};
	} catch (error) {
		const fetchError: RefinedFetchError | undefined = await refineFetchError(error);
		if (fetchError) {
			const { statusCode, responseJson } = fetchError;
			const body: unknown = responseJson;

			if (statusCode === 400) {
				const validationErrors: PrioritySchemeValidationError[] | undefined =
					refineValidationError(body);
				if (validationErrors) {
					return {
						isSuccess: false,
						errors: validationErrors,
					};
				}
			}
		}
		throw error;
	}
};
